/** Session React context — provides state, dispatch, and derived helpers to all session components. */
import { createContext, useContext, useReducer, useMemo, type ReactNode } from 'react';
import { sessionReducer, createInitialState } from './reducer';
import type { SessionState, SessionAction, SessionMode, StepAnswer } from './types';
import type { StudySessionStep } from '@formency/shared';

// ─── Context value ──────────────────────────────────────────────────────────

interface SessionContextValue {
  state: SessionState;
  dispatch: React.Dispatch<SessionAction>;
  currentStep: StudySessionStep | null;
  isAnswerable: boolean;
  hasAnswer: boolean;
  canSubmit: boolean;
  canGoNext: boolean;
  canGoBack: boolean;
  progress: number;
  answeredCount: number;
  frontier: number;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

// ─── Helpers ────────────────────────────────────────────────────────────────

interface InitArg {
  sessionId: string;
  steps: StudySessionStep[];
  mode: SessionMode;
  startIndex?: number;
}

function initFromArg(arg: InitArg): SessionState {
  return createInitialState(arg.sessionId, arg.steps, arg.mode, arg.startIndex);
}

function checkHasAnswer(answer: StepAnswer | null, step: StudySessionStep | null): boolean {
  if (!answer) return false;

  switch (answer.type) {
    case 'mcq':
      return answer.selectedIds.length > 0;
    case 'true_false':
      return answer.selected !== null;
    case 'text':
      return answer.value.trim().length > 0;
    case 'cloze': {
      const clozeConfig = step?.stepSnapshotPublicJson?.config as
        | { blanks?: { id: string }[] }
        | undefined;
      const totalBlanks = clozeConfig?.blanks?.length ?? 0;
      if (totalBlanks === 0) return false;
      return clozeConfig!.blanks!.every((b) => (answer.blanks[b.id] ?? '').length > 0);
    }
    case 'ranking': {
      const config = step?.stepSnapshotPublicJson?.config as { options?: unknown[] } | undefined;
      const totalOptions = config?.options?.length ?? 0;
      return totalOptions > 0 && answer.order.length >= totalOptions;
    }
    case 'classify': {
      const classifyConfig = step?.stepSnapshotPublicJson?.config as
        | { items?: unknown[] }
        | undefined;
      const totalItems = classifyConfig?.items?.length ?? 0;
      return totalItems > 0 && Object.keys(answer.assignments).length >= totalItems;
    }
    case 'cloze_diagram': {
      const cdConfig = step?.stepSnapshotPublicJson?.config as
        | { blanks?: { id: string }[] }
        | undefined;
      const totalBlanks = cdConfig?.blanks?.length ?? 0;
      if (totalBlanks === 0) return false;
      return cdConfig!.blanks!.every((b) => (answer.blanks[b.id] ?? '').length > 0);
    }
    case 'cloze_image': {
      const ciConfig = step?.stepSnapshotPublicJson?.config as
        | { blanks?: { id: string }[] }
        | undefined;
      const totalBlanks = ciConfig?.blanks?.length ?? 0;
      if (totalBlanks === 0) return false;
      return ciConfig!.blanks!.every((b) => (answer.blanks[b.id] ?? '').length > 0);
    }
    case 'image_hotspot':
      return answer.x !== null && answer.y !== null;
    case 'numeric':
      return answer.value !== null;
    case 'info':
      return false;
  }
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface SessionProviderProps {
  sessionId: string;
  steps: StudySessionStep[];
  mode: SessionMode;
  startIndex?: number;
  children: ReactNode;
}

export function SessionProvider({
  sessionId,
  steps,
  mode,
  startIndex,
  children,
}: SessionProviderProps) {
  const [state, dispatch] = useReducer(
    sessionReducer,
    { sessionId, steps, mode, startIndex },
    initFromArg
  );

  const value = useMemo<SessionContextValue>(() => {
    const currentStep = state.steps[state.currentIndex] ?? null;
    const isAnswerable = currentStep?.stepKind === 'question';
    const hasAnswer = checkHasAnswer(state.answer, currentStep);
    const canSubmit = isAnswerable && hasAnswer && state.phase === 'answering';
    const canGoNext =
      state.phase === 'graded' || (currentStep?.stepKind === 'info' && state.phase === 'idle');
    const canGoBack = state.currentIndex > 0;
    const answeredCount = state.answeredSteps.size;
    const progress = state.steps.length > 0 ? answeredCount / state.steps.length : 0;

    return {
      state,
      dispatch,
      currentStep,
      isAnswerable,
      hasAnswer,
      canSubmit,
      canGoNext,
      canGoBack,
      progress,
      answeredCount,
      frontier: state.frontier,
    };
  }, [state, dispatch]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a <SessionProvider>');
  }
  return ctx;
}
